import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Button,
  Stack,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Autocomplete,
  TextField,
  RadioGroup,
  Radio,
  FormControlLabel,
  Grid,
  Typography,
  Chip,
  Tooltip,
  Divider,
} from "@mui/material";
import {
  Check,
  Close,
  Science,
  Lightbulb,
  PowerSettingsNew,
  Tune,
  FactCheck,
  SkipNext,
  Biotech,
} from "@mui/icons-material";
import { DataGrid } from "@mui/x-data-grid";
import { useDispatch, useSelector } from "react-redux";
import { setSelectedCategory } from "../reducers/ruleFrameSlice";
import RangeHistoryDialog from "./RangeHistoryDialog";
import {
  fetchLatest100DaySummaries,
  fetchFilingRules,
  fetchSkipAnalysis,
  fetchRangeHistory,
  setSelected,
  updateFilingRuleStatus,
  deleteFilingRuleStatus,
  exportFilingRulesToExcel,
} from "../reducers/filingRulesSlice";

import { useLocation, useNavigate } from "react-router-dom";

import { GridFooterContainer, GridPagination } from "@mui/x-data-grid";

function CustomFooter({ summaryText, paginationModel, rowCount }) {
  const pageSize = paginationModel?.pageSize || 100;
  const totalPages = Math.max(1, Math.ceil((rowCount || 0) / pageSize));
  const currentPage = (paginationModel?.page || 0) + 1;

    
  return (
    <GridFooterContainer
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        px: 1,
        minHeight: 52,
      }}
    >
      <Typography
        variant="caption"
        sx={{
          color: "text.secondary",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          ml: 1,
        }}
      >
        {summaryText}
      </Typography>

      <Stack direction="row" spacing={1} alignItems="center">
        <Typography
          variant="caption"
          sx={{ color: "text.secondary", whiteSpace: "nowrap" }}
        >
          Page {currentPage} of {totalPages}
        </Typography>
          <GridPagination />
        <Typography
          variant="caption"
          sx={{ color: "text.secondary", whiteSpace: "nowrap" }}
        >
          Click to turn pages
        </Typography>
	  
      </Stack>	
    </GridFooterContainer>
  );
}

const CompactHeader = ({ children }) => (
  <Box
    sx={{
      textAlign: "center",
      lineHeight: 1.5,
      fontSize: "0.72rem",
      fontWeight: 500, // matches default DataGrid header weight, not bolder
      whiteSpace: "normal",
    }}
  >
    {children}
  </Box>
);

const IconHeader = ({ icon, tooltip }) => (
  <Tooltip title={tooltip}>
    <Box sx={{ display: "flex", justifyContent: "center", width: "100%" }}>
      {icon}
    </Box>
  </Tooltip>
);

const BOOL_ICON_COLOR_ON = "rgba(48, 201, 88, 0.9)"; // brighter green
const BOOL_ICON_COLOR_OFF = "rgba(158, 158, 158, 0.6)";

const renderBoolIcon = (params) =>
  params.value ? (
    <Check fontSize="small" sx={{ color: BOOL_ICON_COLOR_ON }} />
  ) : (
    <Close fontSize="small" sx={{ color: BOOL_ICON_COLOR_OFF }} />
  );

const getColumns = (summaries) => [
  {
    field: "final_count",
    flex: 1,
    align: "center",
    headerAlign: "center",
    renderHeader: () => <CompactHeader>No.</CompactHeader>,
  },
  {
    field: "enabled",
    flex: 2,
    align: "center",
    headerAlign: "center",
    renderHeader: () => (
      <IconHeader
        icon={<PowerSettingsNew fontSize="small" sx={{ color: "#fff" }} />}
        tooltip="Status"
      />
    ),
  },
  { field: "category_name", headerName: "Pathology Test Category", flex: 7 },
  { field: "sub_test_name", headerName: "Sub Test", flex: 7 },
  {
    field: "is_advanced",
    flex: 1,
    align: "center",
    headerAlign: "center",
    renderHeader: () => (
      <IconHeader
        icon={<Tune fontSize="small" sx={{ color: "#fff" }} />}
        tooltip="Is Advanced"
      />
    ),
    renderCell: renderBoolIcon,
  },
  {
    field: "needs_review",
    flex: 1,
    align: "center",
    headerAlign: "center",
    renderHeader: () => (
      <IconHeader
        icon={<FactCheck fontSize="small" sx={{ color: "#fff" }} />}
        tooltip="Review & Approval"
      />
    ),
    renderCell: renderBoolIcon,
  },
  {
    field: "skip_without_ranges",
    flex: 1,
    align: "center",
    headerAlign: "center",
    renderHeader: () => (
      <IconHeader
        icon={<SkipNext fontSize="small" sx={{ color: "#fff" }} />}
        tooltip="Skip Without Ranges"
      />
    ),
    renderCell: renderBoolIcon,
  },
  {
    field: "follow_lab",
    flex: 1,
    align: "center",
    headerAlign: "center",
    renderHeader: () => (
      <IconHeader
        icon={<Biotech fontSize="small" sx={{ color: "#fff" }} />}
        tooltip="Follow the Lab File Normal"
      />
    ),
    renderCell: renderBoolIcon,
  },
  {
    field: "lab_min",
    flex: 2.5,
    align: "center",
    headerAlign: "center",
    renderHeader: () => (
      <CompactHeader>
        Lab
        <br />
        Min
      </CompactHeader>
    ),
  },
  {
    field: "lab_max",
    flex: 2.5,
    align: "center",
    headerAlign: "center",
    renderHeader: () => (
      <CompactHeader>
        Lab
        <br />
        Max
      </CompactHeader>
    ),
  },
  {
    field: "min_val",
    flex: 2.5,
    align: "center",
    headerAlign: "center",
    renderHeader: () => (
      <CompactHeader>
        Clinician
        <br />
        Min
      </CompactHeader>
    ),
  },
  {
    field: "max_val",
    flex: 2.5,
    align: "center",
    headerAlign: "center",
    renderHeader: () => (
      <CompactHeader>
        Clinician
        <br />
        Max
      </CompactHeader>
    ),
  },
  {
    field: "previous_check",
    flex: 3.5,
    renderHeader: () => (
      <CompactHeader>
        Previous Test
        <br />
        Result Check
      </CompactHeader>
    ),
  },
  {
    field: "days_or_tests",
    headerName: "Days / Results",
    flex: 2,
    align: "center",
    headerAlign: "center",
  },
  {
    field: "pdiv_below",
    flex: 2,
    align: "center",
    headerAlign: "center",
    renderHeader: () => (
      <CompactHeader>
        %
        <br />
        Below
      </CompactHeader>
    ),
  },
  {
    field: "pdiv_above",
    flex: 2,
    align: "center",
    headerAlign: "center",
    renderHeader: () => (
      <CompactHeader>
        %
        <br />
        Above
      </CompactHeader>
    ),
  },
  { field: "check_type", headerName: "Keyword Rule", flex: 2.7 },
  { field: "keyword", headerName: "Keywords", flex: 6 },
  { field: "file_method", headerName: "File", flex: 5 },
  {
    field: "lab_history_count",
    flex: 1.4,
    align: "center",
    headerAlign: "center",
    sortable: true,
    renderHeader: () => (
      <IconHeader
        icon={<Science fontSize="small" sx={{ color: "#fff" }} />}
        tooltip="Lab Range History records available"
      />
    ),
    renderCell: (params) =>
      params.value > 0 ? (
        <Chip
          label={params.value}
          size="small"
          color="info"
          variant="outlined"
          sx={{ height: 20, fontSize: "0.7rem" }}
        />
      ) : null,
  },
  {
    field: "range_recommended_count",
    flex: 1.4,
    align: "center",
    headerAlign: "center",
    sortable: true,
    renderHeader: () => (
      <IconHeader
        icon={<Lightbulb fontSize="small" sx={{ color: "#fff" }} />}
        tooltip="Suggested ranges available"
      />
    ),
    renderCell: (params) =>
      params.value > 0 ? (
        <Chip
          label={params.value}
          size="small"
          color="warning"
          variant="outlined"
          sx={{ height: 20, fontSize: "0.7rem" }}
        />
      ) : null,
  },
  {
    field: "latest_100_days",
    renderHeader: () => (
      <CompactHeader>
        Latest
        <br />
        Filing Data
      </CompactHeader>
    ),
    flex: 5,
    renderCell: (params) => {
      const category = params.row.category_name;
      const summarySet =
        params.row.enabled === "Enabled"
          ? summaries?.enabled_summary
          : summaries?.full_summary;
      const summary = summarySet?.[category];
      if (!summary) return "";
      const text = `Filed: ${summary.filed} / ${summary.total} (${Math.round(summary["filed_%"])}%)`;
      return (
        <span style={{ cursor: "pointer", color: "#1976d2" }}>{text}</span>
      );
    },
  },
];

// Legend entries shown above the grid (task 3)
const LEGEND_ITEMS = [
  {
    icon: <Science fontSize="small" sx={{ color: "text.secondary" }} />,
    label: "Lab Range History count",
  },
  {
    icon: <Lightbulb fontSize="small" sx={{ color: "text.secondary" }} />,
    label: "Suggestions count",
  },
  {
    icon: (
      <PowerSettingsNew fontSize="small" sx={{ color: "text.secondary" }} />
    ),
    label: "Enabled Status",
  },
  {
    icon: <Tune fontSize="small" sx={{ color: "text.secondary" }} />,
    label: "Is Advanced",
  },
  {
    icon: <FactCheck fontSize="small" sx={{ color: "text.secondary" }} />,
    label: "Review & Approval",
  },
  {
    icon: <SkipNext fontSize="small" sx={{ color: "text.secondary" }} />,
    label: "Skip Without Ranges",
  },
  {
    icon: <Biotech fontSize="small" sx={{ color: "text.secondary" }} />,
    label: "Follow the Lab File Normal",
  },
];

export default function FilingRuleFrame() {
  const dispatch = useDispatch();

  const [openAnalysisDialog, setOpenAnalysisDialog] = useState(false);
  const skipAnalysis = useSelector((state) => state.filingRules.skipAnalysis);
  const summaries = useSelector((state) => state.filingRules.summaries);
  const location = useLocation();
  const navigate = useNavigate();
  const {
    rules,
    loading,
    selected,
    updating,
    exporting,
    rangeHistory,
    rangeHistoryLoading,
  } = useSelector((state) => state.filingRules);

  const [openRangeHistoryDialog, setOpenRangeHistoryDialog] = useState(false);
  const [selectedSubTest, setSelectedSubTest] = useState(null);
  const [selectedFilingCategory, setSelectedFilingCategory] = useState(null);
  const [selectedEnabled, setSelectedEnabled] = useState(false);
  const [selectedRowId, setSelectedRowId] = useState(null); // task 4
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 100,
  });
  const license_key = useSelector(
    (state) => state.authen.loggedInUser?.license_key,
  );
  const has_write = useSelector(
    (state) => state.authen.loggedInUser?.license_role === "Change",
  );

  const [ruleFilters, setRuleFilters] = useState({
    category_name: [],
    sub_test_name: [],
    is_advanced: "",
    needs_review: "",
    enabled: "",
    has_lab_history: false,
    has_suggestions: false,
    has_low_filed: false,
  });

  const getSummaryForRow = React.useCallback(
    (row) => {
      const summarySet =
        row.enabled === "Enabled"
          ? summaries?.enabled_summary
          : summaries?.full_summary;
      return summarySet?.[row.category_name];
    },
    [summaries],
  );

  const lowFiledAvailableCount = React.useMemo(
    () =>
      rules.filter((r) => {
        const summary = getSummaryForRow(r);
        return (
          summary && typeof summary.filed === "number" && summary.filed < 5
        );
      }).length,
    [rules, getSummaryForRow],
  );

  const handleLowFiledFilterToggle = () => {
    setRuleFilters((prev) => ({ ...prev, has_low_filed: !prev.has_low_filed }));
  };

  const categoryNameOptions = React.useMemo(() => {
    const set = new Set(rules.map((r) => r.category_name).filter(Boolean));
    return Array.from(set)
      .sort()
      .map((v) => ({ label: v, value: v }));
  }, [rules]);

  const subTestNameOptions = React.useMemo(() => {
    const set = new Set(rules.map((r) => r.sub_test_name).filter(Boolean));
    return Array.from(set)
      .sort()
      .map((v) => ({ label: v, value: v }));
  }, [rules]);

  const enabledStatusCounts = React.useMemo(() => {
    const counts = {
      Enabled: 0,
      "In Review": 0,
      "Not Configured": 0,
      Disabled: 0,
    };
    rules.forEach((r) => {
      if (counts[r.enabled] !== undefined) counts[r.enabled] += 1;
    });
    return counts;
  }, [rules]);

  const labHistoryAvailableCount = React.useMemo(
    () => rules.filter((r) => (r.lab_history_count || 0) > 0).length,
    [rules],
  );

  const suggestionsAvailableCount = React.useMemo(
    () => rules.filter((r) => (r.range_recommended_count || 0) > 0).length,
    [rules],
  );

  const handleLabHistoryFilterToggle = () => {
    setRuleFilters((prev) => ({
      ...prev,
      has_lab_history: !prev.has_lab_history,
    }));
  };

  const handleSuggestionsFilterToggle = () => {
    setRuleFilters((prev) => ({
      ...prev,
      has_suggestions: !prev.has_suggestions,
    }));
  };

  const filteredRules = React.useMemo(() => {
    const {
      category_name,
      sub_test_name,
      is_advanced,
      needs_review,
      enabled,
      has_lab_history,
      has_suggestions,
      has_low_filed,
    } = ruleFilters;

    return rules.filter((row) => {
      if (
        category_name.length > 0 &&
        !category_name.includes(row.category_name)
      )
        return false;
      if (
        sub_test_name.length > 0 &&
        !sub_test_name.includes(row.sub_test_name)
      )
        return false;

      if (is_advanced !== "") {
        const val = String(Boolean(row.is_advanced));
        if (val !== is_advanced) return false;
      }

      if (needs_review !== "") {
        const val = String(Boolean(row.needs_review));
        if (val !== needs_review) return false;
      }

      if (enabled !== "" && row.enabled !== enabled) return false;
      if (has_lab_history && !((row.lab_history_count || 0) > 0)) return false;
      if (has_suggestions && !((row.range_recommended_count || 0) > 0))
        return false;

      if (has_low_filed) {
        const summary = getSummaryForRow(row);
        if (
          !summary ||
          typeof summary.filed !== "number" ||
          !(summary.filed < 5)
        )
          return false;
      }

      return true;
    });
  }, [rules, ruleFilters]);

  useEffect(() => {
    dispatch(setSelected([]));
    setSelectedFilingCategory(null);
    setSelectedSubTest(null);
    setSelectedEnabled(false);
    setSelectedRowId(null);
    setSummaryText("");
  }, [ruleFilters, dispatch]);

  const handleEnabledFilterToggle = (status) => {
    setRuleFilters((prev) => ({
      ...prev,
      enabled: prev.enabled === status ? "" : status,
    }));
  };

  const enabledButtonColor = {
    Enabled: { active: "success", bg: "rgba(76, 175, 80, 0.15)" },
    "In Review": { active: "warning", bg: "rgba(255, 152, 0, 0.15)" },
    "Not Configured": { active: "secondary", bg: "rgba(233, 30, 99, 0.15)" },
    Disabled: { active: "error", bg: "rgba(244, 67, 54, 0.15)" },
  };

  useEffect(() => {
    if (license_key) {
      dispatch(fetchFilingRules(license_key));
      dispatch(fetchLatest100DaySummaries(license_key));
    }
  }, [dispatch, license_key]);

  useEffect(() => {
    if (!location.state || rules.length === 0) return;

    const { filters, page, selectedRowId, selectedCategory } = location.state;

    if (filters) {
      setRuleFilters(filters);
    }

    if (page !== undefined) {
      setPaginationModel((prev) => ({
        ...prev,
        page,
      }));
    }

    if (selectedCategory) {
      dispatch(setSelectedCategory(selectedCategory));
    }

    if (selectedRowId) {
      const row = rules.find((r) => r.id === selectedRowId);

      if (row) {
        setSelectedRowId(selectedRowId);
        setSelectedFilingCategory(row.category_name);
        setSelectedSubTest(row.sub_test_name);
        setSelectedEnabled(row.enabled === "Enabled");

        dispatch(setSelected([row]));

        setSummaryText(buildSummaryText({ row }));
      }
    }
  }, [location.state, rules, dispatch]);

  const handleRowDoubleClick = (params) => {
    setSelectedRowId(params.row.id); // task 4
    dispatch(setSelectedCategory(params.row.category_name));
    navigate("/dashboard/pathology/Pathology%20Filing%20Rules", {
      state: {
        from: location.pathname,
        selectedRowId: params.row.id,
        selectedCategory: params.row.category_name,
        selectedSubTest: params.row.sub_test_name,
        page: paginationModel.page,
        filters: ruleFilters,
      },
    });
  };

  const handleSelection = (selectionModel) => {
    const selectedIds = Array.from(selectionModel.ids);
    const selectedRowsData = selectedIds
      .map((id) => rules.find((r) => r.id === id))
      .filter(Boolean);
    dispatch(setSelected(selectedRowsData));
  };

  const [summaryText, setSummaryText] = useState("");

  const buildSummaryText = (params) => {
    const category = params.row.category_name;
    const summarySet =
      params.row.enabled === "Enabled"
        ? summaries?.enabled_summary
        : summaries?.full_summary;
    const data = summarySet?.[category];
    if (!data) return "";
    return (
      `${category}: ` +
      `Total ${data.total} | ` +
      `Filed ${data["filed_%"].toFixed(1)}% | ` +
      `Out of Range ${data["not_filed_result_out_of_range_%"].toFixed(1)}% | ` +
      `Rules Missing ${data["not_filed_rules_not_setup_%"].toFixed(1)}% | ` +
      `Clinician Filed ${data["not_filed_already_filed_by_clinician_%"].toFixed(1)}% | ` +
      `Inconclusive ${data["not_filed_inconclusive_report_%"].toFixed(1)}%`
    );
  };

  const handleRowClick = (params) => {
    const category = params.row.category_name;
    const subtest = params.row.sub_test_name;
    setSelectedRowId(params.row.id); // task 4
    setSelectedFilingCategory(category);
    setSelectedSubTest(subtest);
    setSelectedEnabled(params.row.enabled === "Enabled");

    const text = buildSummaryText(params);
    if (text) setSummaryText(text);
  };

  const handleRangeHistory = () => {
    if (!selectedFilingCategory || !selectedSubTest || !license_key) return;
    dispatch(
      fetchRangeHistory({
        category_name: selectedFilingCategory,
        sub_test_name: selectedSubTest,
        license_key,
      }),
    );
    setOpenRangeHistoryDialog(true);
  };

  const handleEnable = () => {
    if (selected.length > 0 && license_key) {
      const categories = [...new Set(selected.map((row) => row.category_name))];
      dispatch(
        updateFilingRuleStatus({ categories, status: "Enabled", license_key }),
      );
    }
  };

  const handleDisable = () => {
    if (selected.length > 0 && license_key) {
      const categories = [...new Set(selected.map((row) => row.category_name))];
      dispatch(
        updateFilingRuleStatus({ categories, status: "Disabled", license_key }),
      );
    }
  };

  const handleDelete = () => {
    if (!selected.length || !license_key) return;
    const categories = [...new Set(selected.map((row) => row.category_name))];
    const result = window.confirm(
      `Are you sure you want to delete the selected filing rules?\n\nCategories:\n${categories.join(", ")}`,
    );
    if (result) {
      dispatch(
        deleteFilingRuleStatus({
          categories,
          rows_data: selected,
          license_key,
        }),
      );
    } else {
      window.alert("The action has been cancelled.");
    }
  };

  const handleFilingAnalysis = () => {
    if (!selectedFilingCategory || !license_key) return;
    dispatch(
      fetchSkipAnalysis({
        category: selectedFilingCategory,
        removeNotSetup: selectedEnabled,
        license_key,
      }),
    );
    setOpenAnalysisDialog(true);
  };

  const handleExportToExcel = () => {
    if (license_key) {
      dispatch(exportFilingRulesToExcel(license_key));
    }
  };

  return (
    <Box
      sx={{
        height: "calc(100vh - 50px)",
        display: "flex",
        flexDirection: "column",
        ml: -1,
        mr: -1,
      }}
    >
      <Paper
        elevation={0}
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          boxShadow: "none",
          border: "none",
          borderRadius: 0, // optional, removes rounded corners
        }}
      >
        <Paper
          variant="outlined"
          sx={{
            p: 1,
            mb: 0,
            mt: 0.5,
            borderRadius: 2,
            borderColor: "#cbd5e0",
            background: "#fafafa",
          }}
        >
          <Grid container spacing={1}>
            <Grid size={{ xs: 8, md: 3 }}>
              <Autocomplete
                multiple
                size="small"
                options={categoryNameOptions}
                getOptionLabel={(option) => option.label || ""}
                filterOptions={(options, state) => {
                  const input = (state.inputValue || "").toLowerCase();
                  return options.filter((option) =>
                    (option.label || "").toLowerCase().includes(input),
                  );
                }}
                value={categoryNameOptions.filter((opt) =>
                  ruleFilters.category_name.includes(opt.value),
                )}
                onChange={(event, newValue) => {
                  setRuleFilters((prev) => ({
                    ...prev,
                    category_name: newValue.map((v) => v.value),
                  }));
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Search Category Name"
                    size="small"
                    sx={{
                      "& .MuiInputBase-root": { fontSize: "0.8rem" },
                      "& .MuiInputLabel-root": {
                        backgroundColor: "#fff",
                        px: 0.5,
                      },
                    }}
                  />
                )}
                fullWidth
              />
            </Grid>

            <Grid size={{ xs: 8, md: 3 }}>
              <Autocomplete
                multiple
                size="small"
                options={subTestNameOptions}
                getOptionLabel={(option) => option.label || ""}
                filterOptions={(options, state) => {
                  const input = (state.inputValue || "").toLowerCase();
                  return options.filter((option) =>
                    (option.label || "").toLowerCase().includes(input),
                  );
                }}
                value={subTestNameOptions.filter((opt) =>
                  ruleFilters.sub_test_name.includes(opt.value),
                )}
                onChange={(event, newValue) => {
                  setRuleFilters((prev) => ({
                    ...prev,
                    sub_test_name: newValue.map((v) => v.value),
                  }));
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Search Pathology Test"
                    size="small"
                    sx={{
                      "& .MuiInputBase-root": { fontSize: "0.8rem" },
                      "& .MuiInputLabel-root": {
                        backgroundColor: "#fff",
                        px: 0.5,
                      },
                    }}
                  />
                )}
                fullWidth
              />
            </Grid>

            <Grid size={{ xs: 8, md: 3 }}>
              <Typography
                variant="caption"
                sx={{
                  color: "#4a5568",
                  fontWeight: 600,
                  display: "block",
                  lineHeight: 1.2,
                }}
              >
                Advanced
              </Typography>
              <RadioGroup
                row
                value={ruleFilters.is_advanced}
                onChange={(e) =>
                  setRuleFilters((prev) => ({
                    ...prev,
                    is_advanced: e.target.value,
                  }))
                }
              >
                <FormControlLabel
                  value=""
                  control={<Radio size="small" sx={{ py: 0.25 }} />}
                  label={<Typography variant="caption">All</Typography>}
                />
                <FormControlLabel
                  value="true"
                  control={<Radio size="small" sx={{ py: 0.25 }} />}
                  label={<Typography variant="caption">Advanced</Typography>}
                />
                <FormControlLabel
                  value="false"
                  control={<Radio size="small" sx={{ py: 0.25 }} />}
                  label={<Typography variant="caption">Normal</Typography>}
                />
              </RadioGroup>
            </Grid>

            <Grid size={{ xs: 8, md: 3 }}>
              <Typography
                variant="caption"
                sx={{
                  color: "#4a5568",
                  fontWeight: 600,
                  display: "block",
                  lineHeight: 1.2,
                }}
              >
                Review & Approval
              </Typography>
              <RadioGroup
                row
                value={ruleFilters.needs_review}
                onChange={(e) =>
                  setRuleFilters((prev) => ({
                    ...prev,
                    needs_review: e.target.value,
                  }))
                }
              >
                <FormControlLabel
                  value=""
                  control={<Radio size="small" sx={{ py: 0.25 }} />}
                  label={<Typography variant="caption">All</Typography>}
                />
                <FormControlLabel
                  value="true"
                  control={<Radio size="small" sx={{ py: 0.25 }} />}
                  label={
                    <Typography variant="caption">Needs Review</Typography>
                  }
                />
                <FormControlLabel
                  value="false"
                  control={<Radio size="small" sx={{ py: 0.25 }} />}
                  label={<Typography variant="caption">No Review</Typography>}
                />
              </RadioGroup>
            </Grid>

            <Grid size={{ xs: 8, md: 12 }}>
              <Stack
                direction="row"
                spacing={0.75}
                flexWrap="wrap"
                alignItems="center"
                useFlexGap
              >
                {Object.entries(enabledStatusCounts).map(([status, count]) => {
                  const isActive = ruleFilters.enabled === status;
                  const colorInfo = enabledButtonColor[status];
                  return (
                    <Button
                      key={status}
                      size="small"
                      variant={isActive ? "contained" : "outlined"}
                      color={colorInfo.active}
                      onClick={() => handleEnabledFilterToggle(status)}
                      sx={{
                        textTransform: "none",
                        py: 0.25,
                        minHeight: 26,
                        backgroundColor: isActive ? undefined : colorInfo.bg,
                      }}
                    >
                      {status} ({count})
                    </Button>
                  );
                })}

                <Divider orientation="vertical" flexItem sx={{ mx: 0.25 }} />

                <Button
                  size="small"
                  variant={
                    ruleFilters.has_lab_history ? "contained" : "outlined"
                  }
                  color="info"
                  startIcon={<Science fontSize="small" />}
                  onClick={handleLabHistoryFilterToggle}
                  sx={{ textTransform: "none", py: 0.25, minHeight: 26 }}
                >
                  Lab Range History ({labHistoryAvailableCount})
                </Button>

                <Button
                  size="small"
                  variant={
                    ruleFilters.has_suggestions ? "contained" : "outlined"
                  }
                  color="warning"
                  startIcon={<Lightbulb fontSize="small" />}
                  onClick={handleSuggestionsFilterToggle}
                  sx={{ textTransform: "none", py: 0.25, minHeight: 26 }}
                >
                  Suggestions ({suggestionsAvailableCount})
                </Button>

                <Button
                  size="small"
                  variant={ruleFilters.has_low_filed ? "contained" : "outlined"}
                  color="error"
                  onClick={handleLowFiledFilterToggle}
                  sx={{ textTransform: "none", py: 0.25, minHeight: 26 }}
                >
                  Filed &lt; 5% ({lowFiledAvailableCount})
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </Paper>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="flex-start"
          sx={{
            position: "sticky",
            top: 0,
            zIndex: 3,
            backgroundColor: "background.paper",
            mb: 0.5,
            px: 0.5,
            py: 0.5,
          }}
        >
          <Stack
            direction="row"
            spacing={1.5}
            flexWrap="wrap"
            sx={{ mb: 0.5, px: 0.5 }}
            useFlexGap
          >
            {LEGEND_ITEMS.map((item) => (
              <Stack
                key={item.label}
                direction="row"
                spacing={0.5}
                alignItems="center"
              >
                {item.icon}
                <Typography variant="caption" color="text.secondary">
                  {item.label}
                </Typography>
              </Stack>
            ))}
          </Stack>
          <Stack
            direction="row"
            spacing={0.75}
            sx={{
              pointerEvents: has_write ? "auto" : "none",
              opacity: has_write ? 1 : 0.3,
            }}
          >
            <Button
              size="small"
              variant="contained"
              color="primary"
              onClick={handleEnable}
              disabled={selected.length === 0 || updating}
              sx={{
                maxHeight: 24,
              }}
            >
              Enable
            </Button>
            <Button
              size="small"
              variant="contained"
              color="warning"
              onClick={handleDisable}
              disabled={selected.length === 0 || updating}
              sx={{
                maxHeight: 24,
              }}
            >
              Disable
            </Button>
            <Button
              size="small"
              variant="contained"
              color="secondary"
              onClick={handleDelete}
              disabled={selected.length === 0 || updating}
              sx={{
                maxHeight: 24,
              }}
            >
              Delete
            </Button>
            <Button
              size="small"
              variant="outlined"
              onClick={handleExportToExcel}
              disabled={exporting || loading}
              sx={{
                maxHeight: 24,
              }}
            >
              {exporting ? "Exporting..." : "Export to Excel"}
            </Button>
            <Button
              size="small"
              variant="contained"
              color="info"
              onClick={handleFilingAnalysis}
              disabled={!selectedFilingCategory}
              sx={{
                maxHeight: 24,
              }}
            >
              Filing Analysis
            </Button>
            <Button
              size="small"
              variant="contained"
              color="info"
              onClick={handleRangeHistory}
              disabled={!selectedFilingCategory || !selectedSubTest}
              sx={{
                maxHeight: 24,
              }}
            >
              Range History
            </Button>
          </Stack>
        </Stack>

        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: "flex", // makes this a flex container itself
            flexDirection: "column",
            width: "100%",
            overflow: "hidden", // clips at this level, not deeper
            position: "relative", // gives ResizeObserver a stable box to measure
          }}
        >
          {loading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "100%",
              }}
            >
              <CircularProgress />
            </Box>
          ) : (
            <DataGrid
              variant="dense"
              rowHeight={26}
              rows={filteredRules}
              columns={getColumns(summaries)}
              paginationModel={paginationModel}
              onPaginationModelChange={setPaginationModel}
              pageSizeOptions={[100]}
              checkboxSelection
              disableRowSelectionOnClick
              onRowSelectionModelChange={handleSelection}
              onRowDoubleClick={handleRowDoubleClick}
              onRowClick={handleRowClick}
              getRowId={(row) => row.id}
              loading={updating}
              getRowClassName={(params) => {
                const evenOdd =
                  params.indexRelativeToCurrentPage % 2 === 0 ? "even" : "odd";
                const status = params.row.enabled;
                let statusClass = "bg-red";
                if (status === "Enabled") statusClass = "bg-green";
                else if (status === "In Review") statusClass = "bg-orange";
                else if (status === "Not Configured") statusClass = "bg-pink";
                const selectedClass =
                  params.row.id === selectedRowId ? "selected-day-row" : "";
                return `${evenOdd} ${statusClass} ${selectedClass}`.trim();
              }}
		slots={{
                footer: () => (
                  <CustomFooter
                    summaryText={summaryText}
                    paginationModel={paginationModel}
                    rowCount={filteredRules.length}
                  />
                ),		    
              }}
              sx={{
                height: "100%",
                border: "none",

                "& .MuiDataGrid-columnHeaders": { marginBottom: "12px" },
                "& .MuiDataGrid-row": { cursor: "pointer" },
                "& .selected-day-row": {
                  backgroundColor: "#fef9c3 !important",
                  outline: "2px solid #7c6fe0",
                  outlineOffset: "-2px",
                  fontWeight: 700,
                },
                "& .selected-day-row:hover": {
                  backgroundColor: "#fef08a !important",
                },
                "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within": {
                  outline: "none",
                },
                "& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within":
                  {
                    outline: "none",
                  },
              }}
            />
          )}
        </Box>
      </Paper>

      <Dialog
        open={openAnalysisDialog}
        onClose={() => setOpenAnalysisDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Skip Analysis Summary — {selectedFilingCategory}
          <IconButton
            aria-label="close"
            onClick={() => setOpenAnalysisDialog(false)}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          {summaryText && (
            <Paper variant="outlined" sx={{ p: 1, mb: 2 }}>
              <Typography variant="body2">{summaryText}</Typography>
            </Paper>
          )}
          <DataGrid
            autoHeight
            rows={skipAnalysis}
            columns={[
              { field: "skip_type", headerName: "Skip Type", flex: 4 },
              { field: "count", headerName: "Count", flex: 1 },
            ]}
            getRowId={(row) => row.skip_type}
            pageSize={20}
          />
        </DialogContent>
      </Dialog>

      <RangeHistoryDialog
        open={openRangeHistoryDialog}
        onClose={() => setOpenRangeHistoryDialog(false)}
        rangeHistory={rangeHistory}
        loading={rangeHistoryLoading}
      />
    </Box>
  );
}
